/**
 * Absences List Page
 *
 * Two concerns:
 *
 * 1. Filter panel UI helpers + cleaner-autocomplete picker.
 *    Filtering and pagination are handled declaratively by HTMX.
 *
 * 2. Absence create / edit modals — canonical ``createDynamicModal``
 *    pattern (see ``form-modals.mdc`` and
 *    ``frontend/src/utils/ui/dynamic-modal.js``). Mirrors what
 *    ``customers.js`` and ``jobs.js`` already do for their own
 *    status-change flows so the office sees the same affordance
 *    everywhere. The shared modal shell lives in
 *    ``templates/office/cleaners/absences/_absence_modal_shells.html``;
 *    the GET endpoints (``office:absence_create`` /
 *    ``office:absence_edit``) render the form body
 *    (``_form_content.html``); the POST endpoints return JSON
 *    ``{success: true, message}`` on success or ``HTTP 422`` + form
 *    HTML on validation errors so the helper paints the response back
 *    inline.
 *
 * Absence cancel stays as a ``showConfirmModal`` confirmation since it
 * has no fields — same UX as Resume Job / Activate Customer.
 *
 * The legacy right-side ``#absence-drawer`` shell + its bespoke
 * fetch/submit pipeline (openAbsenceDrawer, closeAbsenceDrawer,
 * loadDrawerContent, rehydrateDrawerContent, handleDrawerFormSubmit)
 * has been removed and replaced by the helper above.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let cleanerPicker = null;
const state = {
  urls: {},
  translations: {},
};

// Module-level helper for the shared create / edit modal. Created
// lazily on first need (after the shell is present in the DOM) and
// reset on every page-level init so a stale reference to a detached
// modal node from a previous boost cycle never sticks around — see
// ``form-modals.mdc`` §9 ("Boosted-nav module caches"). Same trick as
// ``customers.js`` / ``jobs.js``.
let absenceModal = null;

let afterSettleBound = false;

/**
 * Initialize the absences list page
 */
export function initAbsencesList(options = {}) {
  const { hasActiveFilters = false, urls = {}, translations = {} } = options;

  state.urls = urls;
  state.translations = translations;

  // Reset cached helper so a re-init after an hx-boost swap binds to
  // the fresh modal shell in the new DOM, not to the orphaned one
  // from the previous page life.
  absenceModal = null;

  initFilterPanelUI(hasActiveFilters);
  initCleanerPicker(urls);
  initDatepickerBridge();
  initAbsenceModalTriggers();
  bindAbsencesActionsOnce();
  exposeGlobalFunctions();
}

/**
 * Filter panel toggle / reset (HTMX handles requests)
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('absenceFilterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency flag via plain property — does not serialize
  // into HTMX hx-boost history snapshots.
  if (toggleBtn && panel && !toggleBtn._abFilterBound) {
    toggleBtn._abFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._abResetBound) {
    resetBtn._abResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('select.form-select').forEach((sel) => {
        sel.value = '';
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        if (textEl && sel.options[0]) textEl.textContent = sel.options[0].text;
      });

      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });

      form.querySelectorAll('input[type="hidden"]').forEach((inp) => {
        if (inp.name !== 'view') inp.value = '';
      });

      if (cleanerPicker) cleanerPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/**
 * Initialize cleaner search picker (filter bar)
 */
function initCleanerPicker(urls) {
  const cleanerInput = document.getElementById('cleaner-search');
  if (!cleanerInput) return;

  cleanerPicker = new SearchPicker({
    inputId: 'cleaner-search',
    hiddenInputId: 'cleaner-filter',
    dropdownId: 'cleaner-search-results',
    endpoint: urls.cleanerSearch || '/office/api/cleaners/search/',
    maxResults: 8,
    minChars: 0,
    renderItem: renderCleanerItem,
    onSelect: () => {
      const form = document.getElementById('absenceFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('absenceFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

/**
 * Bridge Flowbite datepicker changeDate → native change for HTMX
 */
function initDatepickerBridge() {
  document.querySelectorAll('[datepicker]').forEach((el) => {
    if (el._abDpBound) return;
    el._abDpBound = true;
    el.addEventListener('changeDate', () => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function renderCleanerItem(item) {
  const name = item.name || item.display_name;
  const initials = item.initials || getInitials(name);
  const photo = item.photo_url || item.avatar_url;

  if (photo) {
    return `
      <div class="flex items-center px-3 py-2">
        <img src="${photo}" alt="" class="w-7 h-7 rounded-full object-cover mr-2.5 flex-shrink-0">
        <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
      </div>
    `;
  }

  return `
    <div class="flex items-center px-3 py-2">
      <div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600">
        <span class="text-xs font-medium text-white">${initials}</span>
      </div>
      <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
    </div>
  `;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ───────────────────────────────────────────────────────────────────────────
// Absence create / edit modal (canonical createDynamicModal)
// ───────────────────────────────────────────────────────────────────────────
//
// Single shared helper hosting both flows in #absence-modal:
//   - Add  → GET ``office:absence_create``  → renders _form_content.html
//   - Edit → GET ``office:absence_edit/<id>`` → same partial, prefilled
//
// The form body ships its own inline <script> (Flowbite Datepicker init,
// cleaner search). createDynamicModal.injectContent() executes any
// <script> in the response, so we don't have to duplicate that wiring
// here. We still pass an ``onContentInjected`` hook to re-attach
// custom-selects + reinit Flowbite tooltips/dropdowns/etc. as a safety
// net (mirrors jobs.js / customers.js).

function ensureAbsenceModalHelper() {
  if (absenceModal) return absenceModal;
  absenceModal = createDynamicModal({
    modalId: 'absence-modal',
    onContentInjected: initFormWidgets,
    onSuccess: handleAbsenceSuccess,
    errorLoadingMessage:
      state.translations.errorLoading || 'Error loading form. Please try again.',
    closeButtonText: state.translations.retry || 'Retry',
  });
  return absenceModal;
}

function initFormWidgets(content) {
  // Phase 17 — boot the absence-form ESM module (all-day toggle +
  // validation clearing + Flowbite Datepicker + cleaner search
  // typeahead). Replaces the inline ``<script>`` that used to live at
  // the bottom of ``_form_content.html``. Idempotent — re-running on a
  // 422 re-render is a no-op thanks to per-element flags.
  if (typeof window.App !== 'undefined'
      && window.App.pages
      && typeof window.App.pages.initAbsenceForm === 'function') {
    window.App.pages.initAbsenceForm({ formId: 'absence-form' });
  }

  if (typeof window.initCustomSelects === 'function') {
    window.initCustomSelects();
  }
  if (typeof window.reinitFlowbiteWidgets === 'function') {
    window.reinitFlowbiteWidgets(content);
  }
}

function handleAbsenceSuccess(data, helper) {
  // Server ships the success message in the JSON (see ``absence_create``
  // / ``absence_edit`` in apps/office/views/absences.py): we toast it
  // directly rather than relying on Django's messages queue +
  // soft-reload, because plain XHR (X-Requested-With) doesn't go
  // through ``HtmxMessagesMiddleware``. See ``toast-feedback.mdc`` §
  // anti-pattern "double-toast".
  if (data && data.message && typeof window.showToast === 'function') {
    window.showToast(data.message, data.message_type || 'success');
  }

  // Two render modes on this page:
  //   - Calendar view (default) → refresh ONLY the timeline grid via
  //     ``window.refreshAbsenceCalendar`` so the cleaner rows / day
  //     columns / scroll position stay put. A full ``_softReload`` would
  //     swap ``#main-content``, leaving the grid empty for the duration
  //     of the async ``/api/absences/events/`` fetch — which the user
  //     perceives as a "white flash + reload" of the calendar.
  //   - List view → no per-row partial endpoint, so we fall back to
  //     ``_softReload`` (one frame swap of the table is fine — there is
  //     no async re-fill pass).
  // In both cases we wait for the close transition to fully complete
  // (``afterClose`` hook on the helper) so the modal backdrop is gone
  // before any DOM mutation fires. Otherwise the swap tears the
  // backdrop down mid-fade and the user perceives a second flash on
  // Pattern B pages where the partial comes back in <100 ms.
  helper.close(() => {
    const calendarView = document.getElementById('absence-calendar-view');
    const calendarVisible = calendarView && !calendarView.classList.contains('hidden');
    if (calendarVisible && typeof window.refreshAbsenceCalendar === 'function') {
      window.refreshAbsenceCalendar();
      return;
    }
    if (typeof window._softReload === 'function') {
      window._softReload();
    }
  });
}

function openAbsenceModal(url, title) {
  const helper = ensureAbsenceModalHelper();
  if (!helper) return;
  helper.open(url, title);
}

function initAbsenceModalTriggers() {
  const createUrl = state.urls.absenceCreate || '/office/cleaners/absences/new/';
  const openBtn = document.getElementById('open-absence-modal-btn');
  if (openBtn && !openBtn._abOpenBound) {
    openBtn._abOpenBound = true;
    openBtn.addEventListener('click', function () {
      openAbsenceModal(
        createUrl,
        state.translations.addAbsence || 'Add Absence',
      );
    });
  }
}

/**
 * Open the absence-edit modal for ``absenceId``.
 *
 * Internal helper, used by both the row-level edit pencil on the
 * absences list (declarative ``data-absences-action="open-edit"``,
 * Phase 21.A) and the calendar block click (still routed through
 * ``window.openEditModal`` from ``absences-timeline.js``).
 */
function openAbsenceEditModal(absenceId, cleanerName) {
  const url = `/office/cleaners/absences/${absenceId}/edit/`;
  const editLabel = state.translations.editAbsence || 'Edit Absence';
  const title = cleanerName ? `${editLabel}: ${cleanerName}` : editLabel;
  openAbsenceModal(url, title);
}

// Delegate at document level for the row-level edit pencil on the
// absences list. Phase 21.A migration of inline
// ``onclick="openEditModal(id, name)"``. Idempotent — bound once
// per SPA lifetime via ``_abActionsBound``.
//
// Bound in the CAPTURE phase to bypass any ancestor
// ``[data-stop-propagation]`` (matches the ``confirm-action.js``
// pattern). The absences list does not currently use a row-level
// drawer-open stopper, but binding in capture keeps the contract
// consistent with the other action delegates in this codebase.
let _abActionsBound = false;

function bindAbsencesActionsOnce() {
  if (_abActionsBound) return;
  _abActionsBound = true;

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-absences-action]');
    if (!trigger) return;
    const action = trigger.dataset.absencesAction;
    if (action !== 'open-edit') return;
    const id = parseInt(trigger.dataset.absenceId, 10);
    if (!Number.isFinite(id)) return;
    const name = trigger.dataset.cleanerName || '';
    openAbsenceEditModal(id, name);
  }, true);
}

function exposeGlobalFunctions() {
  // ``window.openEditModal`` is still consumed from
  // ``pages/absences-timeline.js`` (calendar bar click — see
  // ``absences-timeline.js`` rationale in its module docstring).
  // The absences LIST page no longer goes through ``window`` —
  // its row-level pencil is wired declaratively via
  // ``data-absences-action="open-edit"`` and handled by
  // ``bindAbsencesActionsOnce()`` above.
  window.openEditModal = function (absenceId, cleanerName) {
    openAbsenceEditModal(absenceId, cleanerName);
  };

  // Cell-click in the absences weekly grid (_calendar_view.html) opens
  // the create modal pre-filled with the clicked cleaner + date. The
  // backend (apps/office/views/absences.py::absence_create) reads
  // ?cleaner=<id>&date=YYYY-MM-DD from the URL and seeds the form.
  // Past dates are allowed (retroactive absences).
  window.openCreateAbsenceModal = function (cleanerId, dateStr, cleanerName) {
    const base = state.urls.absenceCreate || '/office/cleaners/absences/new/';
    const params = new URLSearchParams();
    if (cleanerId) params.set('cleaner', String(cleanerId));
    if (dateStr) params.set('date', dateStr);
    const url = `${base}${params.toString() ? '?' + params.toString() : ''}`;
    const addLabel = state.translations.addAbsence || 'Add Absence';
    const title = cleanerName ? `${addLabel}: ${cleanerName}` : addLabel;
    openAbsenceModal(url, title);
  };

  // The list-side cancel button (per-row in the absences table) used
  // to live here as ``window.confirmAbsenceCancel`` — Phase 19 dropped
  // the wrapper. The button now declares ``data-confirm-action="submit-form"``
  // + ``data-confirm-target="cancel-form-<id>"`` directly in the
  // template and is handled by ``utils/ui/confirm-action.js``.
  //
  // The calendar-side cancel below STAYS as a wrapper because it
  // POSTs via ``fetch`` (no <form> in the calendar grid) and runs a
  // side-effect chain (toast + refresh callback) that ``submit-form``
  // can't express. Pattern C (complex callback) — intentionally kept
  // direct.
  window.cancelAbsenceFromCalendar = function (absenceId /* , cleanerName */) {
    if (typeof window.showConfirmModal !== 'function') return;
    window.showConfirmModal({
      title: state.translations.cancelTitle || 'Cancel this absence?',
      message:
        state.translations.cancelMessage ||
        'This will mark the absence as cancelled. The record will be preserved.',
      confirmText: state.translations.cancelConfirm || 'Yes, Cancel Absence',
      type: 'danger',
      onConfirm: function () {
        const url = `/office/cleaners/absences/${absenceId}/cancel/`;
        // Robust CSRF: prefer the cookie (always present after the
        // first Django response) and fall back to a DOM token if any
        // form on the page rendered ``{% csrf_token %}``. The list
        // view's per-row cancel forms do, but they only exist when
        // there's at least one absence — the cookie path covers the
        // empty-list case too.
        const cookieMatch = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
        const csrfToken = cookieMatch
          ? decodeURIComponent(cookieMatch[1])
          : document.querySelector('[name=csrfmiddlewaretoken]')?.value || '';
        fetch(url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': csrfToken || '',
          },
          credentials: 'same-origin',
        })
          .then(async (response) => {
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) {
              if (data.message && typeof window.showToast === 'function') {
                window.showToast(data.message, data.message_type || 'success');
              }
              if (typeof window.refreshAbsenceCalendar === 'function') {
                window.refreshAbsenceCalendar();
              }
            } else if (typeof window.showToast === 'function') {
              const msg = data.message
                || state.translations.genericError
                || 'An error occurred. Please try again.';
              window.showToast(msg, data.message_type || 'error');
            }
          })
          .catch(() => {
            if (typeof window.showToast === 'function') {
              window.showToast(
                state.translations.genericError || 'An error occurred. Please try again.',
                'error',
              );
            }
          });
      },
    });
  };
}

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function () {
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets();
    }
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="absencesList"``).
 */
export function autoInitAbsencesList() {
  const root = document.querySelector('[data-auto-init="absencesList"]');
  if (!root) return;
  const ds = root.dataset;
  initAbsencesList({
    hasActiveFilters: ds.hasActiveFilters === '1',
    urls: {
      cleanerSearch: ds.cleanerSearchUrl || '/office/api/cleaners/search/',
      absenceCreate: ds.absenceCreateUrl || '/office/cleaners/absences/new/',
    },
    translations: {
      addAbsence: ds.trAddAbsence || 'Add Absence',
      editAbsence: ds.trEditAbsence || 'Edit Absence',
      errorLoading: ds.trErrorLoading || 'Error loading form. Please try again.',
      retry: ds.trRetry || 'Retry',
      genericError: ds.trGenericError || 'An error occurred. Please try again.',
      cancelTitle: ds.trCancelTitle || 'Cancel this absence?',
      cancelMessage:
        ds.trCancelMessage
        || 'This will mark the absence as cancelled. The record will be preserved.',
      cancelConfirm: ds.trCancelConfirm || 'Yes, Cancel Absence',
    },
  });
  bindAfterSettleOnce();
}

window.initAbsencesList = initAbsencesList;
